import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useUIVersion } from "../../contexts/UIVersionContext";
import "./tracker-filters.css";

/**
 * TrackerFilters component for Range and BaseShop filtering
 * 
 * Features:
 * - Date range filtering (Today, This Week, This Month, Custom Range)
 * - BaseShop (hierarchy) filtering using existing user relationships
 * - Responsive design with pill-style filters
 * - Real-time filter application
 */
export default function TrackerFilters({
  users = [],
  currentUser = null,
  onRangeChange = null,
  onBaseShopChange = null,
  defaultRange = "all",
  defaultBaseShop = "all",
  className = "",
  layout = "inline"
}) {
  const { isFeatureEnabled } = useUIVersion();
  const filtersEnabled = isFeatureEnabled('modernTrackerFilters');
  
  const [selectedRange, setSelectedRange] = useState(defaultRange);
  const [selectedBaseShop, setSelectedBaseShop] = useState(defaultBaseShop);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showCustomRange, setShowCustomRange] = useState(false);

  // Date range options
  const dateRangeOptions = [
    { key: "all", label: "All Time" },
    { key: "today", label: "Today" },
    { key: "week", label: "This Week" },
    { key: "month", label: "This Month" },
    { key: "quarter", label: "This Quarter" },
    { key: "year", label: "This Year" },
    { key: "custom", label: "Custom Range" }
  ];

  // Calculate date ranges
  const getDateRange = useCallback((rangeKey) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (rangeKey) {
      case "today":
        return {
          start: today,
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      
      case "week":
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        return { start: weekStart, end: weekEnd };
      
      case "month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        monthEnd.setHours(23, 59, 59, 999);
        return { start: monthStart, end: monthEnd };
      
      case "quarter":
        const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
        const quarterStart = new Date(now.getFullYear(), quarterMonth, 1);
        const quarterEnd = new Date(now.getFullYear(), quarterMonth + 3, 0);
        quarterEnd.setHours(23, 59, 59, 999);
        return { start: quarterStart, end: quarterEnd };
      
      case "year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        const yearEnd = new Date(now.getFullYear(), 11, 31);
        yearEnd.setHours(23, 59, 59, 999);
        return { start: yearStart, end: yearEnd };
      
      case "custom":
        if (customStartDate && customEndDate) {
          return {
            start: new Date(customStartDate),
            end: new Date(customEndDate + "T23:59:59.999Z")
          };
        }
        return null;
      
      case "all":
      default:
        return null; // No date filtering
    }
  }, [customStartDate, customEndDate]);

  // BaseShop (hierarchy) options based on user relationships
  const baseShopOptions = useMemo(() => {
    if (!users || users.length === 0) return [{ key: "all", label: "All Teams" }];

    const options = [{ key: "all", label: "All Teams" }];
    
    // Get unique recruiters and leaders from the users data
    const recruiters = new Set();
    const leaders = new Set();
    
    users.forEach(user => {
      if (user.recruiter) recruiters.add(user.recruiter);
      if (user.leader) leaders.add(user.leader);
    });

    // Add hierarchy options
    if (currentUser) {
      options.push({ key: "my-direct", label: "My Direct Team" });
      options.push({ key: "my-downline", label: "My Full Downline" });
    }

    // Add individual recruiters/leaders
    [...recruiters].sort().forEach(recruiter => {
      options.push({ 
        key: `recruiter-${recruiter}`, 
        label: `${recruiter}'s Team` 
      });
    });

    [...leaders].sort().forEach(leader => {
      if (![...recruiters].includes(leader)) { // Avoid duplicates
        options.push({ 
          key: `leader-${leader}`, 
          label: `${leader}'s Group` 
        });
      }
    });

    return options;
  }, [users, currentUser]);

  // Handle range change
  const handleRangeChange = useCallback((rangeKey) => {
    setSelectedRange(rangeKey);
    setShowCustomRange(rangeKey === "custom");
    
    if (rangeKey !== "custom" && onRangeChange) {
      const range = getDateRange(rangeKey);
      onRangeChange(range);
    }
  }, [getDateRange, onRangeChange]);

  // Handle BaseShop change
  const handleBaseShopChange = useCallback((baseShopKey) => {
    setSelectedBaseShop(baseShopKey);
    
    if (onBaseShopChange) {
      onBaseShopChange(baseShopKey);
    }
  }, [onBaseShopChange]);

  // Handle custom date range change
  useEffect(() => {
    if (selectedRange === "custom" && customStartDate && customEndDate && onRangeChange) {
      const range = getDateRange("custom");
      if (range) {
        onRangeChange(range);
      }
    }
  }, [selectedRange, customStartDate, customEndDate, getDateRange, onRangeChange]);

  // Don't render if filters are disabled
  if (!filtersEnabled) {
    return null;
  }

  const containerClass = [
    "tracker-filters",
    layout === "inline" ? "filters-inline" : "filters-stack",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClass}>
      <div className="filter-control">
        <span className="filter-label">Date Range</span>
        <div className="filter-field">
          <select
            className="filter-select"
            value={selectedRange}
            onChange={(e) => handleRangeChange(e.target.value)}
          >
            {dateRangeOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="filter-control">
        <span className="filter-label">Team</span>
        <div className="filter-field">
          <select
            className="filter-select"
            value={selectedBaseShop}
            onChange={(e) => handleBaseShopChange(e.target.value)}
          >
            {baseShopOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {showCustomRange && (
        <div className="filter-control custom-range-block">
          <span className="filter-label secondary">Custom Range</span>
          <div className="custom-range-fields">
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="filter-input"
              placeholder="Start"
            />
            <span className="range-separator">to</span>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="filter-input"
              placeholder="End"
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Utility function to filter data based on date range
 */
export const filterByDateRange = (data, dateFilter, customDateRange, dateField = 'createdAt') => {
  if (!dateFilter || dateFilter === 'all' || !data) return data;
  
  // Handle custom date range
  if (dateFilter === 'custom' && customDateRange) {
    if (!customDateRange.start || !customDateRange.end) return data;
    const startDate = new Date(customDateRange.start);
    const endDate = new Date(customDateRange.end);
    endDate.setHours(23, 59, 59, 999); // Include the entire end date
    
    return data.filter(item => {
      const itemDate = new Date(item[dateField]);
      return itemDate >= startDate && itemDate <= endDate;
    });
  }
  
  // Handle predefined date ranges
  const now = new Date();
  let startDate, endDate;
  
  switch (dateFilter) {
    case 'today':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      break;
    case 'week':
      const dayOfWeek = now.getDay();
      startDate = new Date(now);
      startDate.setDate(now.getDate() - dayOfWeek);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      break;
    case 'quarter':
      const quarter = Math.floor(now.getMonth() / 3);
      startDate = new Date(now.getFullYear(), quarter * 3, 1);
      endDate = new Date(now.getFullYear(), quarter * 3 + 3, 0, 23, 59, 59, 999);
      break;
    case 'year':
      startDate = new Date(now.getFullYear(), 0, 1);
      endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
      break;
    default:
      return data; // Unknown filter, return all data
  }
  
  return data.filter(item => {
    const itemDate = new Date(item[dateField]);
    return itemDate >= startDate && itemDate <= endDate;
  });
};

/**
 * Utility function to filter data based on BaseShop selection
 */
export const filterByBaseShop = (data, baseShopKey, currentUser = null, options = {}) => {
  if (!baseShopKey || baseShopKey === "all" || !data) return data;
  
  // Extract field names from options
  const { recruiterField = 'recruiter', leaderField = 'leader', nested = false } = options;
  
  const getFieldValue = (item, fieldPath) => {
    if (!nested) return item[fieldPath];
    
    // Handle nested paths like 'agents.writing'
    const parts = fieldPath.split('.');
    let value = item;
    for (const part of parts) {
      value = value?.[part];
      if (value === undefined) break;
    }
    return value;
  };
  
  if (baseShopKey === "my-direct" && currentUser) {
    const currentUserName = currentUser.displayName || currentUser.email;
    return data.filter(item => {
      const recruiter = getFieldValue(item, recruiterField);
      const leader = getFieldValue(item, leaderField);
      return recruiter === currentUserName || leader === currentUserName;
    });
  }
  
  if (baseShopKey === "my-downline" && currentUser) {
    const currentUserName = currentUser.displayName || currentUser.email;
    // For downline, we'd need more complex hierarchy logic
    // For now, return direct team + any items where user is mentioned
    return data.filter(item => {
      const recruiter = getFieldValue(item, recruiterField);
      const leader = getFieldValue(item, leaderField);
      return recruiter === currentUserName || 
             leader === currentUserName ||
             item.createdBy === currentUserName;
    });
  }
  
  if (baseShopKey.startsWith("recruiter-")) {
    const recruiterName = baseShopKey.replace("recruiter-", "");
    return data.filter(item => getFieldValue(item, recruiterField) === recruiterName);
  }
  
  if (baseShopKey.startsWith("leader-")) {
    const leaderName = baseShopKey.replace("leader-", "");
    return data.filter(item => getFieldValue(item, leaderField) === leaderName);
  }
  
  return data;
};
